import { useCallback, useEffect, useRef, useState } from "react";
import { loadExtensionsCSV } from "../components/data/dataLoader";

const emptyMetadata = { rowsCount: 0, columns: [] };

const initialState = {
  status: "idle",
  rows: [],
  metadata: emptyMetadata,
  error: null,
};

export function useMarketplaceDataset(datasetPath) {
  const [state, setState] = useState(initialState);
  const controllerRef = useRef(null);

  const computeMetadata = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return emptyMetadata;
    const columnSet = new Set();
    let displayableCount = 0;
    rows.forEach((row) => {
      if (!row) return;
      Object.keys(row).forEach((key) => columnSet.add(key));
      const name = row.name ? String(row.name).trim() : "";
      if (name && name.toLowerCase() !== "name") {
        displayableCount += 1;
      }
    });
    return {
      rowsCount: displayableCount,
      columns: Array.from(columnSet),
    };
  }, []);

  const fetchRows = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((prev) => ({ ...prev, status: "loading", error: null }));

    try {
      const { rows } = await loadExtensionsCSV(datasetPath, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setState({
        status: "success",
        rows,
        metadata: computeMetadata(rows),
        error: null,
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      setState((prev) => ({
        ...prev,
        status: prev.rows.length ? "success" : "error",
        error: err,
      }));
    }
  }, [computeMetadata, datasetPath]);

  useEffect(() => {
    fetchRows();
    return () => controllerRef.current?.abort();
  }, [fetchRows]);

  return {
    ...state,
    refresh: fetchRows,
    hasData: state.rows.length > 0,
  };
}
